import { computed, readonly, ref } from 'vue'
import { api } from '@/lib/api'
import { adminDisplayName, hasAnyPermission, hasPermission, roleSummary } from '@/lib/rbac'
import type { AdminUser, Permission } from '@/types'

const user = ref<AdminUser | null>(null)
const ready = ref(false)
const pending = ref(false)

async function restore(): Promise<void> {
  if (ready.value) return
  try {
    user.value = await api.auth.session()
  } catch {
    user.value = null
  } finally {
    ready.value = true
  }
}

async function login(email: string, password: string): Promise<void> {
  pending.value = true
  try {
    user.value = await api.auth.login(email, password)
    ready.value = true
  } finally {
    pending.value = false
  }
}

async function logout(): Promise<void> {
  pending.value = true
  try {
    await api.auth.logout()
  } finally {
    user.value = null
    pending.value = false
    ready.value = true
  }
}

function expire(): void {
  user.value = null
  pending.value = false
  ready.value = true
}

export const auth = {
  user: readonly(user),
  ready: readonly(ready),
  pending: readonly(pending),
  isAuthenticated: computed(() => user.value !== null),
  displayName: computed(() => user.value ? adminDisplayName(user.value) : '管理员'),
  roleSummary: computed(() => user.value ? roleSummary(user.value.roles) : '未授权'),
  can: (permission?: Permission) => hasPermission(user.value?.roles, permission),
  canAny: (permissions?: readonly Permission[]) => hasAnyPermission(user.value?.roles, permissions),
  restore,
  login,
  logout,
  expire,
  // 仅测试使用，不会被产品页面调用。
  setTestUser(next: AdminUser | null) {
    if (!['development', 'test'].includes(import.meta.env.MODE)) return
    user.value = next
    ready.value = true
  },
}
